import React, { useState, useRef, useEffect } from "react";
import "pagination-pattern/dist/foundation.css";
import chevronLeft from "../../../../public/assets/images/chevronleftsmall.svg";

type DesktopPageItem = number | "ellipsis-left" | "ellipsis-right";

type PaginationProps = {
  currentPage?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange: (page: number) => void;
  hidePaginationCount?: boolean; // Optional prop to hide the count text
};

const getPageRange = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

const getDesktopPageItems = (
  activePage: number,
  totalPages: number,
): DesktopPageItem[] => {
  if (totalPages <= 6) {
    return getPageRange(1, totalPages);
  }

  if (activePage <= 4) {
    return [...getPageRange(1, 5), "ellipsis-right", totalPages];
  }

  if (activePage >= totalPages - 3) {
    return [1, "ellipsis-left", ...getPageRange(totalPages - 4, totalPages)];
  }

  return [
    1,
    "ellipsis-left",
    activePage - 1,
    activePage,
    activePage + 1,
    "ellipsis-right",
    totalPages,
  ];
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage = 1,
  totalItems = 50,
  itemsPerPage = 10,
  hidePaginationCount = false,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [visiblePages, setVisiblePages] = useState<number[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const desktopPageItems = getDesktopPageItems(currentPage, totalPages);

  const handleClick = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    const start = Math.floor((currentPage - 1) / 5) * 5 + 1;
    const end = Math.min(start + 4, totalPages);
    setVisiblePages(getPageRange(start, end));
  }, [currentPage, totalPages]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdownOpen]);

  const fromIndex = (currentPage - 1) * itemsPerPage + 1;
  const toIndex = Math.min(currentPage * itemsPerPage, totalItems);


  if (totalPages <= 1) {
    return null;
  }

  return (
    <div
      className="ds-pagination mobile:w-[10%]! tablet:w-[20%]! desktop:w-full!"
      data-ds-theme="vessel"
      data-ds-variant="navigation"
      data-ds-version="2"
      data-total-items={totalItems}
      data-items-per-page={itemsPerPage}
    >
      {/* COUNT */}
      {!hidePaginationCount && (
        <div className="ds-pagination__count mt-2!">
          <output aria-atomic="true">
            <span className="ds-pagination__fromToIndexTxt">
              {fromIndex} – {toIndex} of {totalItems} items
            </span>
          </output>
        </div>
      )}

      <nav className="ds-pagination__nav" role="navigation">
        {/* DESKTOP LIST */}
        <ul className="ds-pagination__list ds-pagination__desktop-list">
          {/* Prev */}
          <li
            className={`ds-pagination__item -prev ${
              currentPage === 1 ? "-disabled" : ""
            }`}
          >
            <button
              type="button"
              className="ds-pagination__link"
              aria-disabled={currentPage === 1}
              tabIndex={currentPage === 1 ? -1 : 0}
              onClick={() => {
                if (currentPage > 1) {
                  handleClick(currentPage - 1);
                }
              }}
            >
              <span className="screenreader-only">Previous page</span>
              <img
                alt="go to previous page"
                src={chevronLeft}
                width={24}
                height={24}
                aria-hidden="true"
                className="icon-chevron-back"
              />
            </button>
          </li>

          {/* PAGE NUMBERS */}
          {desktopPageItems.map((item, index) => {
            if (typeof item === "number") {
              return (
                <li
                  key={item}
                  className={`ds-pagination__item -page ${
                    currentPage === item ? "-active" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="ds-pagination__link"
                    onClick={() => {
                      handleClick(item);
                    }}
                  >
                    <span className="screenreader-only">
                      {currentPage === item ? "Selected page" : "Page"}
                    </span>
                    <span className="ds-pagination__pageIndex">{item}</span>
                  </button>
                </li>
              );
            }

            return (
              <li
                key={`${item}-${index}`}
                className="ds-pagination__item -ellipsis"
                data-page="ellipsis"
              >
                <button
                  className="ds-pagination__link -not_display_el"
                  onClick={(event) => {
                    event.preventDefault();
                  }}
                >
                  <span className="screenreader-only">Page</span>
                  <span className="ds-pagination__pageIndex">...</span>
                </button>
                <div className="ds-pagination__icon">
                  <span className="screenreader-only">Ellipsis</span>
                  <i className="icon-more"></i>
                </div>
              </li>
            );
          })}

          {/* Next */}
          <li
            className={`ds-pagination__item -next ${
              currentPage === totalPages ? "-disabled" : ""
            }`}
          >
            <button
              type="button"
              className="ds-pagination__link"
              aria-disabled={currentPage === totalPages}
              tabIndex={currentPage === totalPages ? -1 : 0}
              onClick={() => {
                if (currentPage < totalPages) {
                  handleClick(currentPage + 1);
                }
              }}
            >
              <span className="screenreader-only">Next page</span>
              <img
                src={chevronLeft}
                width={24}
                height={24}
                aria-hidden="true"
                className="icon-chevron-forward"
                alt="go to next page"
              />
            </button>
          </li>
        </ul>

        {/* DESKTOP LABELS */}
        {visiblePages.length <= 2 ? (
          <div className="md:flex gap-4">
            <p className="back-label --desktop-label static! inline-flex!">
              Previous results
            </p>
            <p className="forward-label --desktop-label static! inline-flex!">
              Next results
            </p>
          </div>
        ) : (
          <div className="md:flex flex-col">
            <p className="back-label --desktop-label">Previous results</p>
            <p className="forward-label --desktop-label">Next results</p>
          </div>
        )}

        {/* MOBILE LIST */}
        <div className="ds-pagination__list ds-pagination__mobile-list">
          {/* Mobile Prev */}
          <li
            className={`ds-pagination__item -prev ${
              currentPage === 1 ? "-disabled" : ""
            }`}
          >
            <button
              type="button"
              className="ds-pagination__link"
              aria-disabled={currentPage === 1}
              tabIndex={currentPage === 1 ? -1 : 0}
              onClick={() => handleClick(currentPage - 1)}
            >
              <span className="screenreader-only">Previous page</span>
              <img
                src={chevronLeft}
                width={24}
                height={24}
                aria-hidden="true"
                className="icon-chevron-back"
                alt="go to previous page"
              />
            </button>
            <span>
              <p className="back-label mt-2.5!" aria-hidden="true">
                Previous results
              </p>
            </span>
          </li>

          {/* MOBILE DROPDOWN */}
          <div
            ref={dropdownRef}
            className="ds-dropdown"
            data-ds-theme="vessel"
            data-ds-variant="basic"
            data-ds-version="2"
            data-list-type="option"
            data-menu-type="standard"
            data-select-label="selected"
          >
            <div className="ds-dropdown__drop-menu">
              <button
                className="ds-dropdown__trigger-button ds-paginationTrigger"
                type="button"
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
                onClick={() => setDropdownOpen((open) => !open)}
              >
                <span className="ds-dropdown__trigger-text">
                  Page {currentPage}
                </span>
                <img
                  className="ds-dropdown__chevron"
                  src="/assets/images/chevrondown.svg"
                  width="24"
                  height="24"
                  aria-hidden="true"
                  alt="Toggle dropdown"
                />
              </button>

              <ul
                className="ds-dropdown__menu-list paginationList"
                style={{ display: dropdownOpen ? "block" : "none" }}
                tabIndex={-1}
              >
                {/* For large datasets, only show nearby pages instead of all pages */}
                {totalPages <= 100 ? (
                  // Show all pages if there are 100 or fewer
                  getPageRange(1, totalPages).map((page) => (
                    <li key={page}>
                      <button
                        type="button"
                        onClick={() => handleClick(page)}
                        className="drop-menu-list-text"
                        data-selected={page === currentPage}
                      >
                        Page {page}
                      </button>
                    </li>
                  ))
                ) : (
                  // For large datasets, show nearby pages and quick jump input
                  <>
                    {visiblePages.map((page) => (
                      <li key={page}>
                        <button
                          type="button"
                          onClick={() => handleClick(page)}
                          className="drop-menu-list-text"
                          data-selected={page === currentPage}
                        >
                          Page {page}
                        </button>
                      </li>
                    ))}
                    <li className="ds-dropdown__divider my-2" />
                    <li className="px-3 py-2">
                      <input
                        type="number"
                        min={1}
                        max={totalPages}
                        placeholder="Go to page..."
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const pageNum = Number.parseInt((e.target as HTMLInputElement).value);
                            if (pageNum >= 1 && pageNum <= totalPages) {
                              handleClick(pageNum);
                            }
                          }
                        }}
                      />
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* Mobile next */}
          <li
            className={`ds-pagination__item -next ${
              currentPage === totalPages ? "-disabled" : ""
            }`}
          >
            <button
              type="button"
              className="ds-pagination__link"
              aria-disabled={currentPage === totalPages}
              onClick={() => {
                handleClick(currentPage + 1);
              }}
            >
              <span className="screenreader-only">Next page</span>
              <img
                src={chevronLeft}
                width={24}
                height={24}
                aria-hidden="true"
                className="icon-chevron-forward"
                alt="go to next page"
              />
            </button>
            <span>
              <p className="forward-label mt-2.5!" aria-hidden="true">
                Next results
              </p>
            </span>
          </li>
        </div>
      </nav>
         { (
        <div className={`text-[14px] font-gotham float-right! ${totalPages <= 6 ? "mt-1! mr-8!" : 'mt-3! mr-10!'} `}>
          <span className="">
            Total {totalPages} pages
          </span>
        </div>
      )}
    </div>
  );
};

export default Pagination;
